/**
 * Betreuungs-Cockpit - Anpassbares Kachel-Dashboard
 * 
 * Features:
 * - Drag & Drop Kachel-Anordnung
 * - Ein-/Ausblenden einzelner Kacheln
 * - Kompakt/Groß/Volle Breite Größenwahl
 * - Layout wird pro Benutzer gespeichert
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabs } from '@/contexts/TabContext';
import {
  RefreshCw, FileDown, Loader2, Calendar, CalendarDays, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Dashboard Components
import {
  DashboardTile,
  DashboardCustomizePanel,
  FavoritenTile,
  ZuletztAngesehenTile,
  BriefingTile,
  FristenTile,
  AufgabenTile,
  PosteingangTile,
  VerguetungTile,
  SchuldenTile,
  NetzwerkTile,
  WochenberichtTile,
  QRUeberweisungTile,
} from '@/components/dashboard';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function _toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { openTab } = useTabs();
  const { user } = useAuth();
  
  // UI State
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [customRange, setCustomRange] = useState(false);
  const [customVon, setCustomVon] = useState(null);
  const [customBis, setCustomBis] = useState(null);

  // Layout State
  const [tiles, setTiles] = useState([]);
  const [layoutLoaded, setLayoutLoaded] = useState(false);

  // Data State
  const [favoriten, setFavoriten] = useState([]);
  const [zuletztAngesehen, setZuletztAngesehen] = useState([]);
  const [fristen, setFristen] = useState({ dringende: [], naechste: [], zusammenfassung: {} });
  const [aufgaben, setAufgaben] = useState([]);
  const [posteingang, setPosteingang] = useState([]);
  const [verguetung, setVerguetung] = useState(null);
  const [schulden, setSchulden] = useState(null);
  const [netzwerk, setNetzwerk] = useState(null);
  const [stats, setStats] = useState({});

  // Gerichts-Export
  const [klienten, setKlienten] = useState([]);
  const [gerichtsKlient, setGerichtsKlient] = useState('');
  const [gerichtsJahr, setGerichtsJahr] = useState(String(new Date().getFullYear() - 1));
  const [gerichtsExporting, setGerichtsExporting] = useState(false);

  // Navigation Helper
  const nav = useCallback((path, title) => {
    openTab(path, { title });
    navigate(path);
  }, [openTab, navigate]);

  // Layout laden
  const loadLayout = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/dashboard/layout`);
      if (res.ok) {
        const data = await res.json();
        setTiles(data.tiles || []);
      }
    } catch (error) {
      console.error('Layout laden Fehler:', error);
    } finally {
      setLayoutLoaded(true);
    }
  }, []);

  // Layout speichern
  const saveLayout = useCallback(async (newTiles) => {
    try {
      await fetch(`${BACKEND_URL}/api/dashboard/layout`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tiles: newTiles })
      });
    } catch (error) {
      console.error('Layout speichern Fehler:', error);
    }
  }, []);

  // Layout zurücksetzen
  const resetLayout = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/dashboard/layout/reset`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setTiles(data.tiles || []);
        toast.success('Layout zurückgesetzt');
      }
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  }, []);

  // Daten laden
  const loadData = useCallback(async () => {
    setLoading(true);
    const results = await Promise.allSettled([
      fetch(`${BACKEND_URL}/api/favoriten/klienten/details`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/zuletzt-angesehen`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/fristen/dashboard`).then(r => r.json()),
      betreuungAPI.listAufgaben(),
      betreuungAPI.listDokumente({ dokumententyp: 'posteingang' }),
      fetch(`${BACKEND_URL}/api/verguetung/dashboard`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/buchhaltung/schulden-dashboard`).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/netzwerk/statistiken`).then(r => r.json()),
      betreuungAPI.getDashboardStats(),
      betreuungAPI.listKlienten(),
    ]);

    if (results[0].status === 'fulfilled') setFavoriten(results[0].value || []);
    if (results[1].status === 'fulfilled') setZuletztAngesehen(results[1].value || []);
    if (results[2].status === 'fulfilled' && results[2].value.success) {
      setFristen({
        dringende: results[2].value.dringende_fristen || [],
        naechste: results[2].value.naechste_fristen || [],
        zusammenfassung: results[2].value.zusammenfassung || {},
      });
    }
    if (results[3].status === 'fulfilled') setAufgaben(results[3].value || []);
    if (results[4].status === 'fulfilled') setPosteingang(results[4].value || []);
    if (results[5].status === 'fulfilled' && results[5].value.success) setVerguetung(results[5].value);
    if (results[6].status === 'fulfilled') setSchulden(results[6].value);
    if (results[7].status === 'fulfilled') setNetzwerk(results[7].value);
    if (results[8].status === 'fulfilled') setStats(results[8].value || {});
    if (results[9].status === 'fulfilled') setKlienten(results[9].value || []);
    
    setLoading(false);
  }, []);

  useEffect(() => {
    loadLayout();
    loadData();
  }, [loadLayout, loadData]);

  // Tile Handlers
  const handleTileMove = useCallback((dragIndex, hoverIndex) => {
    setTiles(prev => {
      const newTiles = [...prev];
      const [draggedTile] = newTiles.splice(dragIndex, 1);
      newTiles.splice(hoverIndex, 0, draggedTile);
      // Order aktualisieren
      const reordered = newTiles.map((t, i) => ({ ...t, order: i }));
      saveLayout(reordered);
      return reordered;
    });
  }, [saveLayout]);

  const handleTileSizeChange = useCallback((id, size) => {
    setTiles(prev => {
      const newTiles = prev.map(t => t.id === id ? { ...t, size } : t);
      saveLayout(newTiles);
      return newTiles;
    });
  }, [saveLayout]);

  const handleTileVisibilityChange = useCallback((id, visible) => {
    setTiles(prev => {
      const newTiles = prev.map(t => t.id === id ? { ...t, visible } : t);
      saveLayout(newTiles);
      return newTiles;
    });
  }, [saveLayout]);

  // Protokoll Export
  const handleExportProtokoll = async (typ) => {
    setExporting(true);
    setExportOpen(false);
    try {
      const name = user ? `${user.vorname} ${user.nachname}` : 'System Administrator';
      const now = new Date();
      let von, bis;

      if (typ === 'tag') {
        von = bis = _toISO(now);
      } else if (typ === 'woche') {
        const day = now.getDay();
        const diffMon = day === 0 ? -6 : 1 - day;
        const montag = new Date(now);
        montag.setDate(now.getDate() + diffMon);
        von = _toISO(montag);
        bis = _toISO(now);
      } else if (typ === 'monat') {
        von = _toISO(new Date(now.getFullYear(), now.getMonth(), 1));
        bis = _toISO(now);
      } else if (typ === 'custom' && customVon && customBis) {
        von = _toISO(customVon);
        bis = _toISO(customBis);
      } else {
        von = bis = _toISO(now);
      }

      const params = new URLSearchParams({ betreuer_name: name, von, bis, typ });
      const response = await fetch(`${BACKEND_URL}/api/cockpit/tagesprotokoll?${params}`);
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const labels = { tag: 'Tagesprotokoll', woche: 'Wochenprotokoll', monat: 'Monatsprotokoll', custom: 'Taetigkeitsbericht' };
      a.download = `${labels[typ] || 'Protokoll'}_${von.replace(/-/g, '')}_${bis.replace(/-/g, '')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF-Export fehlgeschlagen');
    } finally {
      setExporting(false);
    }
  };

  // Gerichts-Export
  const handleGerichtsExport = async () => {
    if (!gerichtsKlient) { toast.error('Bitte Klient auswählen'); return; }
    setGerichtsExporting(true);
    try {
      const name = user ? `${user.vorname} ${user.nachname}` : 'System Administrator';
      const params = new URLSearchParams({ jahr: gerichtsJahr, betreuer_name: name });
      const response = await fetch(`${BACKEND_URL}/api/buchhaltung/gerichts-export/${gerichtsKlient}?${params}`);
      if (!response.ok) throw new Error('Export fehlgeschlagen');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const kl = klienten.find(k => k.id === gerichtsKlient);
      const klName = kl ? `${kl.nachname}_${kl.vorname}` : 'Klient';
      const a = document.createElement('a');
      a.href = url;
      a.download = `Gerichts-Export_${klName}_${gerichtsJahr}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Gerichts-Export fehlgeschlagen');
    } finally {
      setGerichtsExporting(false);
    }
  };

  // Favorit entfernen
  const handleRemoveFavorit = async (klientId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/favoriten/klienten/${klientId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setFavoriten(prev => prev.filter(k => k.id !== klientId));
        toast.success('Favorit entfernt');
      }
    } catch (error) {
      toast.error('Fehler beim Entfernen des Favoriten');
    }
  };

  // Briefing Daten zusammenstellen
  const briefingData = {
    fristen: fristen.zusammenfassung,
    aufgaben: aufgaben.filter(a => a.status !== 'erledigt').length,
    post: posteingang.filter(p => !p.gelesen && p.status !== 'bearbeitet').length,
    schulden: schulden?.ueberfaellig?.anzahl || 0,
    klienten: stats.klienten_aktiv || 0,
  };

  // Render Tile Content
  const renderTileContent = (tile) => {
    const isCompact = tile.size === 'compact';
    
    switch (tile.id) {
      case 'favoriten':
        return <FavoritenTile data={favoriten} nav={nav} onRemoveFavorit={handleRemoveFavorit} />;
      case 'zuletzt_angesehen':
        return <ZuletztAngesehenTile data={zuletztAngesehen} nav={nav} />;
      case 'briefing':
        return <BriefingTile data={briefingData} user={user} nav={nav} />;
      case 'fristen':
        return <FristenTile data={fristen} nav={nav} compact={isCompact} />;
      case 'aufgaben':
        return <AufgabenTile data={aufgaben} nav={nav} compact={isCompact} />;
      case 'posteingang':
        return <PosteingangTile data={posteingang} nav={nav} compact={isCompact} />;
      case 'verguetung':
        return <VerguetungTile data={verguetung} nav={nav} compact={isCompact} />;
      case 'schulden':
        return <SchuldenTile data={schulden} nav={nav} compact={isCompact} />;
      case 'netzwerk':
        return <NetzwerkTile data={netzwerk} nav={nav} />;
      case 'wochenbericht':
        return <WochenberichtTile data={aufgaben} nav={nav} />;
      case 'qr_ueberweisung':
        return <QRUeberweisungTile nav={nav} />;
      default:
        return <div className="p-4 text-sm text-slate-400">Unbekannte Kachel: {tile.id}</div>;
    }
  };

  // Tiles sortieren und filtern
  const sortedTiles = [...tiles].sort((a, b) => a.order - b.order);
  const visibleTiles = sortedTiles.filter(t => t.visible);
  
  // Favoriten immer oben
  const favoritenTile = visibleTiles.find(t => t.id === 'favoriten');
  const otherTiles = visibleTiles.filter(t => t.id !== 'favoriten');

  if (!layoutLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="cockpit-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl font-bold text-slate-800">Startseite</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Protokoll Export */}
          <Popover open={exportOpen} onOpenChange={setExportOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting}>
                {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                Protokoll
                <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="end">
              {!customRange ? (
                <div className="p-1">
                  <button className="w-full text-left px-3 py-2 rounded hover:bg-slate-100 text-sm" onClick={() => handleExportProtokoll('tag')}>
                    <p className="font-medium">Tagesprotokoll</p>
                    <p className="text-xs text-slate-400">Heute</p>
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded hover:bg-slate-100 text-sm" onClick={() => handleExportProtokoll('woche')}>
                    <p className="font-medium">Wochenprotokoll</p>
                    <p className="text-xs text-slate-400">Aktuelle Woche</p>
                  </button>
                  <button className="w-full text-left px-3 py-2 rounded hover:bg-slate-100 text-sm" onClick={() => handleExportProtokoll('monat')}>
                    <p className="font-medium">Monatsprotokoll</p>
                    <p className="text-xs text-slate-400">Aktueller Monat</p>
                  </button>
                  <div className="border-t my-1" />
                  <button className="w-full text-left px-3 py-2 rounded hover:bg-slate-100 text-sm" onClick={() => setCustomRange(true)}>
                    <p className="font-medium">Benutzerdefiniert</p>
                    <p className="text-xs text-slate-400">Zeitraum wählen</p>
                  </button>
                </div>
              ) : (
                <div className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium">Zeitraum</p>
                    <button onClick={() => setCustomRange(false)} className="text-xs text-violet-600">Zurück</button>
                  </div>
                  <div className="space-y-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-start">
                          {customVon ? _toISO(customVon) : 'Von...'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><CalendarPicker mode="single" selected={customVon} onSelect={setCustomVon} /></PopoverContent>
                    </Popover>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="w-full justify-start">
                          {customBis ? _toISO(customBis) : 'Bis...'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0"><CalendarPicker mode="single" selected={customBis} onSelect={setCustomBis} /></PopoverContent>
                    </Popover>
                    <Button size="sm" className="w-full" onClick={() => handleExportProtokoll('custom')} disabled={!customVon || !customBis}>
                      <FileDown className="w-4 h-4 mr-2" /> PDF generieren
                    </Button>
                  </div>
                </div>
              )}
            </PopoverContent>
          </Popover>

          <DashboardCustomizePanel
            tiles={sortedTiles}
            onTileVisibilityChange={handleTileVisibilityChange}
            onReset={resetLayout}
            isEditing={isEditing}
            onEditingChange={setIsEditing}
          />

          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Favoriten - immer oben */}
      {favoritenTile && (
        <DashboardTile
          id="favoriten"
          index={0}
          size="full"
          isEditing={false}
        >
          {renderTileContent(favoritenTile)}
        </DashboardTile>
      )}

      {/* Andere Kacheln im Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {otherTiles.map((tile, index) => (
          <DashboardTile
            key={tile.id}
            id={tile.id}
            index={index}
            size={tile.size}
            isEditing={isEditing}
            onMove={handleTileMove}
            onSizeChange={handleTileSizeChange}
          >
            {renderTileContent(tile)}
          </DashboardTile>
        ))}
      </div>

      {/* Gerichts-Export Card */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-slate-700 mb-2">Gerichts-Export-Paket (ZIP)</h3>
            <div className="flex flex-wrap gap-2">
              <Select value={gerichtsKlient} onValueChange={setGerichtsKlient}>
                <SelectTrigger className="w-48 h-8 text-sm">
                  <SelectValue placeholder="Klient wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {klienten.map(k => (
                    <SelectItem key={k.id} value={k.id}>{k.vorname} {k.nachname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={gerichtsJahr} onValueChange={setGerichtsJahr}>
                <SelectTrigger className="w-24 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...Array(5)].map((_, i) => {
                    const y = String(new Date().getFullYear() - i);
                    return <SelectItem key={y} value={y}>{y}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={handleGerichtsExport} disabled={gerichtsExporting || !gerichtsKlient}>
                {gerichtsExporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                ZIP herunterladen
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
